import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BellRing, Clock, Flag } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/aba";

export const RemindersPanel = ({ patientId, compact = false }) => {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["reminders", patientId || "all"],
    queryFn: async () => (await api.get("/reminders", { params: patientId ? { patient_id: patientId } : {} })).data,
  });
  const items = data?.items || [];
  const scadenze = data?.scadenze || [];
  if (isLoading || (items.length === 0 && scadenze.length === 0)) return null;
  const shownItems = compact ? items.slice(0, 4) : items;
  const shownDeadlines = compact ? scadenze.slice(0, 4) : scadenze;
  const late = scadenze.filter((s) => s.in_ritardo).length;
  const go = (r) => navigate(`/pazienti/${r.patient_id}?dominio=${r.domain}&azione=${r.action_id}`);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      {scadenze.length > 0 && (
        <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} data-testid="deadlines-panel">
          <Card className={`h-full ${late ? "border-rose-200 bg-rose-50/60" : "border-amber-200 bg-amber-50/60"}`}>
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-start gap-3">
                <span className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${late ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}><Flag className="h-5 w-5" /></span>
                <div className="flex-1 min-w-0">
                  <h3 className={`font-heading font-semibold ${late ? "text-rose-950" : "text-amber-950"}`}>
                    Obiettivi programmati: {late > 0 ? `${late} in ritardo` : ""}{late > 0 && scadenze.length - late > 0 ? " · " : ""}{scadenze.length - late > 0 ? `${scadenze.length - late} in scadenza` : ""}
                  </h3>
                  <p className={`text-sm ${late ? "text-rose-900/80" : "text-amber-900/80"}`}>Azioni non ancora acquisite rispetto alla data obiettivo pianificata.</p>
                  <ul className="mt-3 grid grid-cols-1 gap-2">
                    {shownDeadlines.map((r) => (
                      <li key={r.action_id}>
                        <button type="button" onClick={() => go(r)} className="w-full text-left rounded-lg bg-card border border-border hover:border-primary/40 px-3 py-2 flex items-center justify-between gap-3 transition-colors" data-testid={`deadline-${r.action_id}`}>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{r.nome} <span className="text-muted-foreground font-normal">· {r.domain_label}</span></p>
                            <p className="text-xs text-muted-foreground truncate">{!patientId && <>{r.patient_nome} · </>}obiettivo {fmtDate(r.data_obiettivo)}</p>
                          </div>
                          <span className={`inline-flex items-center gap-1 text-xs font-semibold shrink-0 ${r.in_ritardo ? "text-rose-800" : "text-amber-800"}`}>
                            {r.in_ritardo ? `${Math.abs(r.giorni_all_obiettivo)} gg di ritardo` : r.giorni_all_obiettivo === 0 ? "scade oggi" : `tra ${r.giorni_all_obiettivo} gg`} <ArrowRight className="h-3.5 w-3.5" />
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                  {compact && scadenze.length > shownDeadlines.length && <p className="text-xs text-muted-foreground mt-2">+{scadenze.length - shownDeadlines.length} altri obiettivi</p>}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {items.length > 0 && (
        <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} data-testid="reminders-panel">
          <Card className="h-full border-rose-200 bg-rose-50/60">
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-start gap-3">
                <span className="h-9 w-9 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center shrink-0"><BellRing className="h-5 w-5" /></span>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-semibold text-rose-950">Promemoria: {items.length} {items.length === 1 ? "azione senza dati" : "azioni senza dati"} da oltre {data.soglia_giorni} giorni</h3>
                  <p className="text-sm text-rose-900/80">Riprendi la raccolta dati per non perdere continuità nell'apprendimento.</p>
                  <ul className="mt-3 grid grid-cols-1 gap-2">
                    {shownItems.map((r) => (
                      <li key={r.action_id}>
                        <button type="button" onClick={() => go(r)} className="w-full text-left rounded-lg bg-card border border-rose-200/70 hover:border-rose-300 px-3 py-2 flex items-center justify-between gap-3 transition-colors" data-testid={`reminder-${r.action_id}`}>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{r.nome} <span className="text-muted-foreground font-normal">· {r.domain_label}</span></p>
                            <p className="text-xs text-muted-foreground truncate">{!patientId && <>{r.patient_nome} · </>}{r.ultima_data ? `ultima sessione ${fmtDate(r.ultima_data)}` : `introdotta il ${fmtDate(r.data_introduzione)}, nessuna sessione`}</p>
                          </div>
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-rose-800 shrink-0"><Clock className="h-3.5 w-3.5" /> {r.giorni_inattivita} gg <ArrowRight className="h-3.5 w-3.5" /></span>
                        </button>
                      </li>
                    ))}
                  </ul>
                  {compact && items.length > shownItems.length && <p className="text-xs text-rose-900/70 mt-2">+{items.length - shownItems.length} altre azioni da riprendere</p>}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
};
